import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Alert,
    Anchor,
    Badge,
    Button,
    Divider,
    Grid,
    Group,
    NumberInput,
    SegmentedControl,
    Select,
    Stack,
    Table,
    Text,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { applyMonitoringPlan } from '../../api/resources';
import { DataPanel } from '../../components/DataPanel';
import { PageHeader } from '../../components/PageHeader';
import {
    MONITORING_FREQUENCY_VALUES,
    MONITORING_INTERVAL_UNIT_VALUES,
    MonitoringFrequency,
    MonitoringIntervalUnit,
} from '../../types/domain';

/** Rows shown before collapsing into a "+N more selected" line, as in the spec. */
const PREVIEW_ROWS = 5;

/**
 * Cases handed over by the Cases page selection or the bulk import flow.
 * Only the fields rendered in the summary table are required.
 */
type MonitoringSettingsState = {
    cases?: Array<{
        id: string;
        externalReferenceId?: string | null;
        caseType?: string;
        status?: string;
    }>;
};

export function MonitoringSettingsPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const location = useLocation();

    const cases = (location.state as MonitoringSettingsState | null)?.cases ?? [];

    const [enabled, setEnabled] = useState('enable');
    const [frequency, setFrequency] = useState<string>(MonitoringFrequency.Monthly);
    const [intervalEvery, setIntervalEvery] = useState<number | string>(10);
    const [intervalUnit, setIntervalUnit] = useState<string>(MonitoringIntervalUnit.Days);
    const [startDate, setStartDate] = useState<string | null>(null);
    const [endDate, setEndDate] = useState<string | null>(null);

    /** The selection lives in router state, so a refresh has nothing to act on. */
    useEffect(() => {
        if (cases.length === 0) {
            navigate('/cases', { replace: true });
        }
    }, [cases.length, navigate]);

    const isCustom = frequency === MonitoringFrequency.Custom;

    const applyMutation = useMutation({
        mutationFn: () =>
            applyMonitoringPlan({
                caseIds: cases.map((c) => c.id),
                enabled: enabled === 'enable',
                frequency,
                ...(isCustom
                    ? {
                          customInterval: {
                              every: Number(intervalEvery) || 1,
                              unit: intervalUnit,
                          },
                      }
                    : {}),
                startDate: startDate ?? undefined,
                endDate: endDate ?? undefined,
            }),
        onSuccess: (result) => {
            notifications.show({
                color: 'green',
                message: t('features.monitoringSettings.applied', {
                    updated: result.updated,
                    visits: result.visitsGenerated,
                }),
            });
            void queryClient.invalidateQueries({ queryKey: ['cases'] });
            navigate('/cases');
        },
        onError: (e: Error) => notifications.show({ color: 'red', message: e.message }),
    });

    const datesValid = !startDate || !endDate || startDate <= endDate;
    const canSubmit = cases.length > 0 && datesValid && !applyMutation.isPending;

    if (cases.length === 0) {
        return null;
    }

    return (
        <Stack gap="lg">
            <Anchor
                component="button"
                type="button"
                c="dimmed"
                fz="sm"
                fw={600}
                onClick={() => navigate('/cases')}
                style={{ alignSelf: 'flex-start' }}
            >
                <Group gap={6}>
                    <IconArrowLeft size={15} />
                    {t('common.actions.back')}
                </Group>
            </Anchor>

            <PageHeader
                title={t('features.monitoringSettings.title')}
                description={t('features.monitoringSettings.description')}
            />

            <Grid gap="lg" align="flex-start">
                {/* Left — the selection being acted on (1.4fr in the spec). */}
                <Grid.Col span={{ base: 12, lg: 7 }}>
                    <DataPanel>
                        <Text fw={700} size="md" mb="md">
                            {t('features.monitoringSettings.selectedCases', {
                                count: cases.length,
                            })}
                        </Text>
                        <Table withTableBorder>
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th>{t('fields.externalReference')}</Table.Th>
                                    <Table.Th>{t('fields.caseType')}</Table.Th>
                                    <Table.Th>{t('fields.status')}</Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {cases.slice(0, PREVIEW_ROWS).map((c) => (
                                    <Table.Tr key={c.id}>
                                        <Table.Td>{c.externalReferenceId ?? c.id}</Table.Td>
                                        <Table.Td>
                                            {c.caseType ? (
                                                <Badge variant="light">
                                                    {t(`caseTypes.${c.caseType}`, {
                                                        defaultValue: c.caseType,
                                                    })}
                                                </Badge>
                                            ) : (
                                                t('common.empty')
                                            )}
                                        </Table.Td>
                                        <Table.Td>
                                            {c.status
                                                ? t(`caseStatuses.${c.status}`, {
                                                      defaultValue: c.status,
                                                  })
                                                : t('common.empty')}
                                        </Table.Td>
                                    </Table.Tr>
                                ))}
                            </Table.Tbody>
                        </Table>
                        {cases.length > PREVIEW_ROWS ? (
                            <Text size="sm" c="dimmed" ta="center" mt="sm">
                                {t('features.monitoringSettings.moreSelected', {
                                    count: cases.length - PREVIEW_ROWS,
                                })}
                            </Text>
                        ) : null}
                    </DataPanel>
                </Grid.Col>

                {/* Right — the plan being applied (1fr in the spec). */}
                <Grid.Col span={{ base: 12, lg: 5 }}>
                    <DataPanel>
                        <Stack>
                            <Text fw={700} size="md">
                                {t('features.monitoringSettings.planSettings')}
                            </Text>

                            <Stack gap={6}>
                                <Text size="sm" fw={500}>
                                    {t('features.monitoringSettings.monitoringStatus')}
                                </Text>
                                <SegmentedControl
                                    value={enabled}
                                    onChange={setEnabled}
                                    data={[
                                        {
                                            value: 'enable',
                                            label: t('features.monitoringSettings.enable'),
                                        },
                                        {
                                            value: 'pause',
                                            label: t('features.monitoringSettings.pause'),
                                        },
                                    ]}
                                />
                            </Stack>

                            <Select
                                label={t('features.monitoringSettings.frequency')}
                                data={MONITORING_FREQUENCY_VALUES.map((value) => ({
                                    value,
                                    label: t(`monitoringFrequencies.${value}`),
                                }))}
                                value={frequency}
                                onChange={(v) => setFrequency(v ?? MonitoringFrequency.Monthly)}
                                allowDeselect={false}
                            />

                            {isCustom ? (
                                <Stack gap={6}>
                                    <Text size="sm" fw={500}>
                                        {t('features.monitoringSettings.customInterval')}
                                    </Text>
                                    <Group gap="sm" align="flex-end" wrap="nowrap">
                                        <Text size="sm" c="dimmed" pb={8}>
                                            {t('features.monitoringSettings.every')}
                                        </Text>
                                        <NumberInput
                                            w={90}
                                            min={1}
                                            value={intervalEvery}
                                            onChange={setIntervalEvery}
                                        />
                                        <Select
                                            style={{ flex: 1 }}
                                            data={MONITORING_INTERVAL_UNIT_VALUES.map((value) => ({
                                                value,
                                                label: t(`monitoringIntervalUnits.${value}`),
                                            }))}
                                            value={intervalUnit}
                                            onChange={(v) =>
                                                setIntervalUnit(v ?? MonitoringIntervalUnit.Days)
                                            }
                                            allowDeselect={false}
                                        />
                                    </Group>
                                </Stack>
                            ) : null}

                            {isCustom ? (
                                <Alert color="yellow" variant="light">
                                    {t('features.monitoringSettings.customHint')}
                                </Alert>
                            ) : null}

                            <Group grow align="flex-start">
                                <DatePickerInput
                                    label={t('features.monitoringSettings.startDate')}
                                    clearable
                                    value={startDate}
                                    onChange={setStartDate}
                                />
                                <DatePickerInput
                                    label={t('features.monitoringSettings.endDate')}
                                    clearable
                                    value={endDate}
                                    onChange={setEndDate}
                                    error={
                                        datesValid
                                            ? undefined
                                            : t('features.monitoringSettings.endBeforeStart')
                                    }
                                />
                            </Group>

                            <Divider />

                            <Button
                                fullWidth
                                loading={applyMutation.isPending}
                                disabled={!canSubmit}
                                onClick={() => applyMutation.mutate()}
                            >
                                {t('features.monitoringSettings.saveAction')}
                            </Button>
                        </Stack>
                    </DataPanel>
                </Grid.Col>
            </Grid>
        </Stack>
    );
}
